import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';

import { api } from '../lib/api';
import { getSession } from '../lib/session';
import { setFormatLocale } from '../lib/format';
import { en, type StringKey } from '../content/strings/en';
import { ne } from '../content/strings/ne';
import {
  LOCALES, LOCALE_NAMES, isLocale, readStoredLocale, resolveLocale, translator,
  writeStoredLocale, type Locale,
} from '../lib/i18n';

/**
 * The language the app is being read in.
 *
 * Resolved once at start-up — an explicit choice, then the browser's
 * languages, then English — and changed only from the picker. The whole app
 * re-renders on a change rather than reloading: a reload would throw away an
 * exam in progress, which is the one thing this product cannot do to somebody.
 */

const DICTS: Record<Locale, Partial<Record<StringKey, string>>> = { en, ne };

interface LocaleValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: StringKey, vars?: Record<string, string | number>) => string;
}

const Ctx = createContext<LocaleValue | null>(null);

const store = () => (typeof localStorage === 'undefined' ? null : localStorage);

export default function LocaleProvider({ children }: { children: ReactNode }) {
  /*
   * Resolved lazily in the initialiser rather than in an effect. An effect
   * would render the whole app in English and then swap it, which on a slow
   * phone is a visible flash of the wrong language on every single load.
   */
  const [locale, setLocaleState] = useState<Locale>(() => resolveLocale({
    stored: readStoredLocale(store()),
    preferred: typeof navigator === 'undefined' ? [] : navigator.languages ?? [],
  }));

  const setLocale = useCallback((next: Locale) => {
    /*
     * Checked at RUNTIME although the parameter is typed, because the one
     * caller reads a `<select>` value and a type is not a guard. An
     * unrecognised locale used to reach three places and spoil each of them:
     * `DICTS[locale]` became undefined and every `t()` threw — the whole app
     * replaced by the error boundary — while `document.documentElement.lang`
     * took the junk string, and `writeStoredLocale` persisted it, so the
     * damage survived a reload. (`readStoredLocale` validates, so the stored
     * copy was inert; the live session was not.)
     *
     * Ignored rather than coerced to English. Somebody's language is not
     * ours to change on the strength of a value we cannot explain, and a
     * picker that appears to do nothing is a far smaller failure than one
     * that silently switches a Nepali reader to English.
     */
    if (!isLocale(next)) return;

    setLocaleState(next);
    writeStoredLocale(store(), next);

    /*
     * Tell the server too, when there is somebody to tell it about.
     *
     * The browser's copy governs what THIS device shows; the server's governs
     * what a renewal reminder composed at 07:00 by a background job is
     * written in. Storing it only here would leave every notification in
     * English however clearly somebody chose otherwise.
     *
     * Fire and forget: a language preference is not worth an error box, and
     * the choice has already applied locally either way.
     */
    if (getSession()) {
      void api('/account/profile', { method: 'POST', body: { locale: next } }).catch(() => {});
    }
  }, []);

  /*
   * `lang` on the document, not just in React state. It is what a screen
   * reader picks a voice from and what the browser hyphenates by — and
   * Devanagari read aloud in an English voice is unintelligible rather than
   * merely wrong.
   */
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  /*
   * Set during RENDER, not in the effect above. Children render after this
   * line and before any effect runs, so an effect would format the first
   * paint's dates in the previous language. Idempotent, so a repeat render
   * or a strict-mode double call is harmless.
   */
  setFormatLocale(locale);

  const value = useMemo<LocaleValue>(() => ({
    locale,
    setLocale,
    /*
     * This lookup is the one that failed. It is `Record<Locale, …>` indexed
     * by a `Locale`, so the compiler is certain it is defined — and it was
     * undefined in a live browser, because the value had come through a cast
     * at the picker. `translator` now treats an absent table as "no
     * translations, use English", so the worst case here is the wrong
     * language rather than no application.
     */
    t: translator(en, DICTS[locale] as Record<string, string> | undefined),
  }), [locale, setLocale]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function useLocaleContext(): LocaleValue {
  const ctx = useContext(Ctx);
  if (ctx) return ctx;
  /*
   * A component rendered outside the provider gets working English rather
   * than a crash. Missing copy is a defect; a blank screen because a modal
   * mounted somewhere unexpected is an outage.
   */
  return { locale: 'en', setLocale: () => {}, t: translator(en, en) };
}

/** The translate function. `const t = useT()`, then `t('nav.help')`. */
export function useT() {
  return useLocaleContext().t;
}

/** The active language and how to change it — for the picker. */
export function useLocale() {
  const { locale, setLocale } = useLocaleContext();
  return { locale, setLocale, locales: LOCALES, names: LOCALE_NAMES };
}
